/**
 * Dựng chuỗi VietQR ngay trên máy, không gọi dịch vụ ngoài.
 *
 * Lý do làm tại chỗ: máy bán hàng có thể đang mất mạng trong khi điện thoại khách
 * vẫn có 4G. Nếu mã QR phải tải từ máy chủ về thì đúng lúc cần nhất lại không có.
 *
 * Định dạng theo EMVCo, mỗi trường là bộ ba: mã (2 số) · độ dài (2 số) · nội dung.
 */

const GUID_NAPAS = 'A000000727'
const SERVICE_TO_ACCOUNT = 'QRIBFTTA'
const CURRENCY_VND = '704'
const COUNTRY_VN = 'VN'

const field = (id: string, value: string): string =>
  `${id}${String(value.length).padStart(2, '0')}${value}`

/** CRC16/CCITT-FALSE — đa thức 0x1021, khởi tạo 0xFFFF. Napas kiểm tra trường này. */
function crc16(input: string): string {
  let crc = 0xffff
  for (let i = 0; i < input.length; i++) {
    crc ^= input.charCodeAt(i) << 8
    for (let bit = 0; bit < 8; bit++) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0')
}

/** Bỏ dấu tiếng Việt: trường nội dung chuyển khoản chỉ nhận chữ không dấu. */
export function stripAccents(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .replace(/[^0-9A-Za-z ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export interface VietQrInput {
  /** Mã ngân hàng 6 số theo Napas (VCB 970436, TCB 970407, MB 970422…). */
  bankBin: string
  accountNo: string
  amount?: number
  description?: string
}

export function buildVietQr({ bankBin, accountNo, amount, description }: VietQrInput): string {
  const beneficiary = field('00', bankBin) + field('01', accountNo)
  const merchant = field('00', GUID_NAPAS) + field('01', beneficiary) + field('02', SERVICE_TO_ACCOUNT)

  let payload =
    field('00', '01') +
    // 11 = mã dùng lại nhiều lần, 12 = mã một lần cho đúng hoá đơn này.
    field('01', amount && amount > 0 ? '12' : '11') +
    field('38', merchant) +
    field('53', CURRENCY_VND)

  if (amount && amount > 0) payload += field('54', String(Math.round(amount)))

  payload += field('58', COUNTRY_VN)

  if (description) {
    const clean = stripAccents(description).slice(0, 25)
    if (clean) payload += field('62', field('08', clean))
  }

  payload += '6304'
  return payload + crc16(payload)
}

/** Vài ngân hàng hay gặp ở quán nhỏ, để chủ quán chọn thay vì tra mã BIN. */
export const BANKS: { bin: string; short: string; name: string }[] = [
  { bin: '970436', short: 'Vietcombank', name: 'Ngân hàng Ngoại thương Việt Nam' },
  { bin: '970422', short: 'MB Bank', name: 'Ngân hàng Quân đội' },
  { bin: '970407', short: 'Techcombank', name: 'Ngân hàng Kỹ thương Việt Nam' },
  { bin: '970416', short: 'ACB', name: 'Ngân hàng Á Châu' },
  { bin: '970415', short: 'VietinBank', name: 'Ngân hàng Công thương Việt Nam' },
  { bin: '970418', short: 'BIDV', name: 'Ngân hàng Đầu tư và Phát triển' },
  { bin: '970405', short: 'Agribank', name: 'Ngân hàng Nông nghiệp' },
  { bin: '970432', short: 'VPBank', name: 'Ngân hàng Việt Nam Thịnh vượng' },
  { bin: '970423', short: 'TPBank', name: 'Ngân hàng Tiên Phong' },
  { bin: '970403', short: 'Sacombank', name: 'Ngân hàng Sài Gòn Thương Tín' },
]
